import { useRouterState } from "@tanstack/react-router"
import { ChevronRight, Lock as LockIcon, ShieldCheck } from "lucide-react"
import { Fragment, type Ref, useEffect, useRef, useState } from "react"

import PayApplePay from "@/assets/img/compatibility/checkout/pay-applepay.svg"
import PayMaestro from "@/assets/img/compatibility/checkout/pay-maestro.svg"
import PayMastercard from "@/assets/img/compatibility/checkout/pay-mastercard.svg"
import PayVisa from "@/assets/img/compatibility/checkout/pay-visa.svg"
import Adv1 from "@/assets/img/soulmate/checkout/adv-1.png"
import VerifiedIcon from "@/assets/img/checkout/verified.svg"
import Adv2 from "@/assets/img/soulmate/checkout/adv-2.png"
import Adv3 from "@/assets/img/soulmate/checkout/adv-3.png"
import Adv4 from "@/assets/img/soulmate/checkout/adv-4.png"
import Adv5 from "@/assets/img/soulmate/checkout/adv-5.png"
import Gift from "@/assets/img/soulmate/checkout/gift.png"
import GuaranteeShield from "@/assets/img/soulmate/checkout/guarantee-shield.svg"
import HeroBg from "@/assets/img/soulmate/checkout/hero-bg.png"
import HeroSketch from "@/assets/img/soulmate/checkout/hero-sketch.png"
import HeroSketchMale from "@/assets/img/soulmate/checkout/hero-sketch-male.png"
import Payment2Head from "@/assets/img/soulmate/checkout/payment2-head.png"
import User1 from "@/assets/img/soulmate/checkout/user-1.png"
import User2 from "@/assets/img/soulmate/checkout/user-2.png"
import User3 from "@/assets/img/soulmate/checkout/user-3.png"
import StarGold from "@/assets/img/soulmate/star-gold.svg"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { hasAdditionalOffers } from "@/features/offers"
import { usePlanSelection } from "@/features/products"
import type { ProductPlan } from "@/features/products/types"
import { cn } from "@/lib/utils"
import { LoadingSpinner } from "@/shared/components/loading-spinner"
import { MobileLayout } from "@/shared/components/mobile-layout"
import { isValidEmail } from "@/shared/lib/email"
import { formatMoney } from "@/shared/lib/money"
import { AppRoutes } from "@/shared/lib/routes"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { LegalLink } from "@/shared/components/legal-link"
import { usePurchaseStore } from "@/stores/purchase-store"
import { useQuizStore } from "@/stores/quiz-store"
import { buildConsentDisclaimer } from "../domain/disclaimer"
import { savingsPercent, strikePrice, trialDurationPhrase } from "../domain/pricing"
import { isApplePayAvailable, useCheckout } from "../hooks/use-checkout"
import { ApplePaySheet } from "./apple-pay-sheet"
import { CardPaymentSheet } from "./card-payment-sheet"
import { MoneyBackGuarantee } from "./money-back-guarantee"
import { PaymentButtons } from "./payment-buttons"

type OpenSheet = "card" | "applepay" | null

/** What the paywall unlocks (Figma Subscription advantages card). */
const ADVANTAGES = [
  { icon: Adv1, title: "Unlock your soulmate sketch", text: "See the face of your future partner" },
  { icon: Adv2, title: "Personality & love traits", text: "Understand how they think, love, and connect" },
  { icon: Adv3, title: "Compatibility insights", text: "Discover strengths and emotional challenges" },
  { icon: Adv4, title: "Meeting signs & timing", text: "Clues about when your paths may cross" },
  { icon: Adv5, title: "Private personal reading", text: "One-to-one chat with our relationship specialist" },
]

const REVIEWS = [
  {
    avatar: User1,
    name: "Jasmine Turner",
    text: "I honestly wasn't expecting the sketch to feel this personal. The eyes, the energy, even the vibe felt strangely familiar — like someone I'm meant to meet.",
  },
  {
    avatar: User2,
    name: "Rachel Sullivan",
    text: "The personality reading surprised me even more than the sketch. It described the kind of partner I always end up attracted to with scary accuracy.",
  },
  {
    avatar: User3,
    name: "Daniel Carter",
    text: "What stood out most were the meeting signs and timing clues. It felt less like a random prediction and more like a story that actually fits my life.",
  },
]

function ReviewStars() {
  return (
    <div className="flex">
      {Array.from({ length: 5 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed 5-star row
        <img key={i} src={StarGold} alt="" className="size-4" />
      ))}
    </div>
  )
}

/** mm:sec countdown shown in the navbar — two boxed digits with a dotted divider. */
function Countdown({ seconds }: { seconds: number }) {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0")
  const ss = String(seconds % 60).padStart(2, "0")
  const box = (value: string, label: string) => (
    <div className="flex h-10 w-12 flex-col items-center justify-center rounded-lg bg-purple-light/10">
      <span className="-mb-1 font-display text-xl font-medium text-purple-light tabular-nums">
        {value}
      </span>
      <span className="text-xs text-purple-light/40">{label}</span>
    </div>
  )
  return (
    <div className="flex items-center gap-1">
      {box(mm, "min")}
      <div className="flex flex-col gap-0.5">
        <span className="size-0.5 rounded-full bg-purple-light/40" />
        <span className="size-0.5 rounded-full bg-purple-light/40" />
        <span className="size-0.5 rounded-full bg-purple-light/40" />
      </div>
      {box(ss, "sec")}
    </div>
  )
}

/** "Total due today: ——— $42.99 › $1.00 / N% SAVED" row, from live plan data. */
function PriceRow({ plan }: { plan: ProductPlan }) {
  const strike = strikePrice(plan)
  const savings = savingsPercent(plan)
  return (
    <div className="flex items-center gap-4">
      <span className="text-base font-semibold text-purple-light">Total due today:</span>
      <div className="h-px flex-1 bg-purple-light/20" />
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-1">
          {strike != null && (
            <span className="text-base text-purple-light/40 line-through decoration-peach">
              {formatMoney(strike, plan.currency)}
            </span>
          )}
          <ChevronRight className="size-3 text-purple-light/40" />
          <span className="text-base font-semibold text-white">
            {formatMoney(plan.price, plan.currency)}
          </span>
        </div>
        {savings > 0 && (
          <span className="rounded-full bg-green/20 px-2 py-0.5 text-[10px] font-semibold text-green">
            {savings}% SAVED
          </span>
        )}
      </div>
    </div>
  )
}

/** Secure-payment + money-back card shown under each pay block. */
function SecurePaymentCard({ plan }: { plan: ProductPlan }) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-3xl border border-white/10 p-4">
      <p className="text-sm font-semibold text-white">Safe &amp; Secure Payment</p>
      <div className="flex w-full gap-1">
        {[
          { src: PayVisa, cls: "h-3 w-[37px]" },
          { src: PayMastercard, cls: "h-6 w-[31px]" },
          { src: PayMaestro, cls: "h-6 w-[31px]" },
          { src: PayApplePay, cls: "h-4 w-[38px]" },
        ].map((p) => (
          <div
            key={p.src}
            className="flex h-10 flex-1 items-center justify-center rounded-lg bg-white/10"
          >
            <img src={p.src} alt="" className={p.cls} />
          </div>
        ))}
      </div>
      <div className="h-px w-full bg-white/10" />
      <MoneyBackGuarantee />
      <p className="text-center text-xs text-purple-light/40">{buildConsentDisclaimer(plan)}</p>
    </div>
  )
}

/**
 * The Soulmate Sketch checkout — the `soulmate` checkout variant: the Sketch
 * subscription paywall (Figma Subscription) with the real pay buttons. The
 * pay-what-you-want amount picker (Figma Price) now lives earlier, as the quiz's
 * `price` step (`soulmate-price-step.tsx`). Prices/disclaimers come from the live
 * plan; transactional parts reuse the shared payment primitives; purchase
 * analytics fire inside `useCheckout`.
 */
export function SoulmateCheckout() {
  const navigate = useNavigateWithParams()
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>
  const purchaseStatus = usePurchaseStore((s) => s.status)
  const email = (useQuizStore((s) => s.answers.email) as string) || ""
  // Blurred future-partner sketch follows the chosen partner gender; female is the
  // default — it also covers "Doesn't matter" (any image), mirroring the email hero.
  const partnerGender = useQuizStore((s) => s.answers["partner-gender"])
  const heroSketch = partnerGender === "male" ? HeroSketchMale : HeroSketch
  const { selectedPlan, isLoading } = usePlanSelection()

  const [sheet, setSheet] = useState<OpenSheet>(null)
  const [applePayAvailable] = useState(isApplePayAvailable)
  const { payWithApplePay } = useCheckout(selectedPlan ?? undefined, email, { initCard: false })

  const paymentRef = useRef<HTMLDivElement>(null)
  const scrollToPayment = () =>
    paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
  const [seconds, setSeconds] = useState(9 * 60 + 45)
  const [paymentOnScreen, setPaymentOnScreen] = useState(false)

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const el = paymentRef.current
    if (!el) return
    const io = new IntersectionObserver(([entry]) => setPaymentOnScreen(entry.isIntersecting), {
      rootMargin: "0px 0px -40% 0px",
    })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const nextRoute = hasAdditionalOffers()
    ? AppRoutes.ADDITIONAL_OFFERS
    : typeof search.upsell === "string" && search.upsell
      ? AppRoutes.UPSELL
      : AppRoutes.CREATE_ACCOUNT

  useEffect(() => {
    if (!selectedPlan) return
    trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
      productId: selectedPlan.id,
      price: selectedPlan.price,
      currency: selectedPlan.currency,
    })
    // The paywall is the only screen here (no discount stage) — shown on mount.
    trackEvent(ANALYTICS_EVENTS.SOULMATE_SUB_SCREEN_SHOWN)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id])

  useEffect(() => {
    if (purchaseStatus === "success") navigate({ to: nextRoute })
  }, [purchaseStatus, navigate, nextRoute])

  const handleSuccess = () => {
    setSheet(null)
    navigate({ to: nextRoute })
  }

  const handleApplePay = async () => {
    trackEvent(ANALYTICS_EVENTS.SOULMATE_SUB_SCREEN_SUB_BUTTON_CLICK, {
      metadata: { method: "apple" },
    })
    if (isValidEmail(email)) {
      const ok = await payWithApplePay()
      if (ok) handleSuccess()
    } else {
      setSheet("applepay")
    }
  }

  if (isLoading || !selectedPlan) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-dark-base">
        <LoadingSpinner />
      </div>
    )
  }

  const renderPay = (ref?: Ref<HTMLDivElement>) => (
    <div className="space-y-4">
      <PriceRow plan={selectedPlan} />
      <div ref={ref}>
        <PaymentButtons
          applePayAvailable={applePayAvailable}
          onApplePay={handleApplePay}
          onCard={() => {
            trackEvent(ANALYTICS_EVENTS.SOULMATE_SUB_SCREEN_SUB_BUTTON_CLICK, {
              metadata: { method: "card" },
            })
            setSheet("card")
          }}
        />
      </div>
    </div>
  )

  return (
    <div className="min-h-[100dvh] bg-dark-base font-body text-purple-light">
      <div
        className={cn(
          "sticky top-0 z-20 border-purple-light/10 border-b bg-dark-base/90 pt-safe backdrop-blur transition-all duration-200",
          paymentOnScreen && "pointer-events-none -translate-y-full opacity-0",
        )}
      >
        <MobileLayout className="flex items-center justify-between py-2">
          <Countdown seconds={seconds} />
          <button
            type="button"
            onClick={scrollToPayment}
            className="flex h-10 w-[180px] items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
          >
            Get my results
          </button>
        </MobileLayout>
      </div>

      <MobileLayout className="relative isolate flex flex-col pt-10 pb-safe">
        {/* Hero */}
        <img
          src={HeroBg}
          alt=""
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[240px] w-full object-cover"
        />
        <div className="mx-auto w-fit rounded-full bg-green/20 px-3 py-2 text-center text-[10px] font-medium uppercase tracking-wide text-green backdrop-blur">
          Your reading is ready
        </div>
        <h1 className="mt-5 text-center font-display text-2xl font-medium text-purple-light">
          Unlock your <span className="text-main-purple">Soulmate Sketch</span>!
        </h1>
        <p className="mt-2 text-center text-base text-purple-light/70">
          Reveal the face and hidden traits of your future partner.
        </p>

        <div className="relative mx-auto mt-6 h-[308px] w-full max-w-[382px] overflow-hidden rounded-[24px] border-2 border-peach">
          <img src={heroSketch} alt="" className="size-full scale-110 object-cover blur-md" />
          <div className="absolute inset-0 bg-dark-base/40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-10 items-center gap-2 rounded-[28px] bg-dark-base/80 pr-4 pl-3">
              <LockIcon className="size-5 text-white" />
              <span className="text-sm font-semibold text-white">Unlock to reveal</span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            trackEvent(ANALYTICS_EVENTS.SOULMATE_SUB_SCREEN_BUTTON_CLICK)
            scrollToPayment()
          }}
          className="mt-6 h-14 w-full rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
        >
          Reveal my results
        </button>
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-purple-light/40">
          <ShieldCheck className="size-4" />
          We do not collect your biometric data
        </div>

        {/* What you unlock */}
        <h2 className="mt-10 text-center font-display text-2xl font-medium text-purple-light">
          See what you can uncover and enjoy inside
        </h2>
        <div className="mt-4 flex flex-col gap-2 rounded-[20px] bg-dark-surface p-4">
          {ADVANTAGES.map((a, i) => (
            <Fragment key={a.title}>
              {i > 0 && <div className="h-px w-full bg-purple-light/10" />}
              <div className="flex items-center gap-2.5">
                <div className="flex size-[52px] shrink-0 items-center justify-center overflow-hidden rounded-[26px] bg-dark-base">
                  <img src={a.icon} alt="" className="size-10 object-contain mix-blend-lighten" />
                </div>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-white">{a.title}</p>
                  <p className="text-sm text-purple-light/70">{a.text}</p>
                </div>
              </div>
            </Fragment>
          ))}
        </div>

        {/* Gift banner */}
        <div className="mt-4 overflow-hidden rounded-2xl">
          <div className="bg-green py-1 text-center text-xs font-bold uppercase text-dark-base">
            Personal gift included
          </div>
          <div className="flex items-end gap-2 bg-gradient-to-r from-purple-light/5 to-purple-light/20 pr-4">
            <img src={Gift} alt="" className="-my-2 size-28 shrink-0 -rotate-12 object-contain" />
            <div className="flex flex-col gap-0.5 py-3">
              <p className="font-display text-base font-medium text-purple-light">
                Unlock everything for {formatMoney(selectedPlan.price, selectedPlan.currency)}
              </p>
              <p className="text-sm text-purple-light/70">
                {`Including ${trialDurationPhrase(selectedPlan)}trial personal reading. Ask your question and get guidance.`}
              </p>
            </div>
          </div>
        </div>

        {/* Primary price block */}
        <div className="mt-8">{renderPay(paymentRef)}</div>
        <div className="mt-4">
          <SecurePaymentCard plan={selectedPlan} />
        </div>

        {/* Testimonials */}
        <div className="-mx-4 mt-10 flex flex-col gap-6 bg-dark-surface px-4 py-10">
          <h2 className="font-display text-[32px] font-medium leading-tight text-purple-light">
            See What Others Revealed About Their Soulmate
          </h2>
          <div className="flex items-center gap-2">
            <img src={StarGold} alt="" className="size-8" />
            <span className="font-display text-2xl font-medium">
              <span className="text-gold">4.8</span>
              <span className="text-purple-light"> (2545 readings)</span>
            </span>
          </div>
          <div className="no-scrollbar -mr-4 flex gap-4 overflow-x-auto pr-4 pb-2">
            {REVIEWS.map((r) => (
              <div
                key={r.name}
                className="flex w-[300px] shrink-0 flex-col justify-between gap-6 rounded-2xl border border-purple-light/20 p-4"
              >
                <p className="text-base text-purple-light/70">{r.text}</p>
                <div className="flex items-center gap-2">
                  <img src={r.avatar} alt="" className="size-11 rounded-full object-cover" />
                  <div className="flex flex-col gap-1">
                    <span className="font-display text-base font-medium text-purple-light">
                      {r.name}
                    </span>
                    <div className="flex items-center gap-1">
                      <ReviewStars />
                      <span className="text-sm font-medium text-gold">4.8</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm">
            <span className="font-medium text-white">*Disclaimer: </span>
            <span className="text-purple-light/70">
              Every soulmate reading is unique and based on your personal answers and compatibility
              analysis.
            </span>
          </p>
        </div>

        {/* Second offer */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <img
            src={Payment2Head}
            alt=""
            className="-mx-4 -mb-10 w-[calc(100%+2rem)] max-w-none object-cover"
          />
          <div className="space-y-2 text-center">
            <h2 className="font-display text-2xl font-medium text-purple-light">
              Reveal the face of your soulmate
            </h2>
            <p className="text-base text-purple-light/70">
              Unlock your personalized sketch, compatibility insights, and private reading with Live
              Palmistry.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-4 rounded-3xl bg-dark-surface p-5">
          {renderPay()}
          <p className="text-center text-xs text-purple-light/40">
            {buildConsentDisclaimer(selectedPlan)}
          </p>
        </div>

        {/* Satisfaction guarantee */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <img src={GuaranteeShield} alt="" className="size-20" />
            <h2 className="font-display text-2xl font-medium text-purple-light">
              Satisfaction Guaranteed
            </h2>
            <p className="text-base text-purple-light/70">
              At Live Palmistry, every sketch, compatibility insight, and personal reading is
              designed to feel personal, meaningful, and worth exploring. If your experience doesn't
              feel magical or insightful, we're here for you.
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 rounded-[20px] bg-purple-light/10 px-4 pt-2 pb-3">
            <div className="text-center">
              <p className="font-display text-2xl font-medium text-purple-light">4.8 out of 5</p>
              <p className="text-sm text-purple-light/70">personal readings revealed worldwide</p>
            </div>
            <div className="h-px w-full bg-purple-light/10" />
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <ReviewStars />
                  <span className="text-sm font-medium text-gold">4.8</span>
                </div>
                <p className="text-xs text-purple-light/40">Based on user experiences</p>
              </div>
              <div className="flex items-center gap-1 rounded-2xl bg-green/10 py-1.5 pr-2 pl-1.5">
                <img src={VerifiedIcon} alt="" className="size-4" />
                <span className="text-xs font-semibold text-green">Verified reviews</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-10 flex flex-col gap-2 text-xs text-purple-light/40">
          <p className="text-center">All rights reserved. 2026</p>
          <div className="h-px w-full bg-purple-light/10" />
          <div className="flex text-center">
            <LegalLink to={AppRoutes.PRIVACY} className="flex-1 hover:text-purple-light">
              Privacy Policy
            </LegalLink>
            <LegalLink to={AppRoutes.TERMS} className="flex-1 hover:text-purple-light">
              Terms of use
            </LegalLink>
            <LegalLink to={AppRoutes.COOKIES} className="flex-1 hover:text-purple-light">
              Cookie Policy
            </LegalLink>
          </div>
        </footer>
      </MobileLayout>

      <CardPaymentSheet
        plan={selectedPlan}
        email={email}
        open={sheet === "card"}
        onClose={() => setSheet(null)}
        onSuccess={handleSuccess}
      />
      <ApplePaySheet
        plan={selectedPlan}
        email={email}
        open={sheet === "applepay"}
        onClose={() => setSheet(null)}
        onSuccess={handleSuccess}
      />
    </div>
  )
}
